import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';

const Canvas = styled.canvas`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
`;

const INTENSITY_CONFIG = {
    low: { particles: 15, speedMultiplier: 0.5 },
    medium: { particles: 25, speedMultiplier: 1 },
    high: { particles: 40, speedMultiplier: 1.5 }
};

const HEART_COLORS = [
    'rgba(255, 107, 139, 0.8)',
    'rgba(255, 107, 139, 0.6)',
    'rgba(107, 91, 255, 0.7)',
    'rgba(255, 182, 193, 0.7)'
];

const HeartsEffect = ({ intensity = 'medium' }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const particlesRef = useRef([]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const config = INTENSITY_CONFIG[intensity] || INTENSITY_CONFIG.medium;

        const resize = () => {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const drawHeart = (x, y, size, color, rotation) => {
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(rotation);
            ctx.beginPath();
            ctx.moveTo(0, -size * 0.3);
            ctx.bezierCurveTo(size * 0.5, -size, size, -size * 0.3, 0, size * 0.6);
            ctx.bezierCurveTo(-size, -size * 0.3, -size * 0.5, -size, 0, -size * 0.3);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.restore();
        };

        const createParticles = () => {
            particlesRef.current = [];
            for (let i = 0; i < config.particles; i++) {
                particlesRef.current.push({
                    x: Math.random() * canvas.width,
                    y: canvas.height + Math.random() * 100,
                    size: Math.random() * 12 + 8,
                    speed: (Math.random() * 1.5 + 0.5) * config.speedMultiplier,
                    wobble: Math.random() * Math.PI * 2,
                    wobbleSpeed: Math.random() * 0.02 + 0.01,
                    rotation: Math.random() * 0.4 - 0.2,
                    color: HEART_COLORS[Math.floor(Math.random() * HEART_COLORS.length)]
                });
            }
        };
        createParticles();

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            particlesRef.current.forEach(particle => {
                drawHeart(
                    particle.x + Math.sin(particle.wobble) * 20,
                    particle.y,
                    particle.size,
                    particle.color,
                    particle.rotation
                );

                particle.y -= particle.speed;
                particle.wobble += particle.wobbleSpeed;

                if (particle.y < -30) {
                    particle.y = canvas.height + 30;
                    particle.x = Math.random() * canvas.width;
                }
            });

            animationRef.current = requestAnimationFrame(animate);
        };
        animate();

        return () => {
            window.removeEventListener('resize', resize);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [intensity]);

    return <Canvas ref={canvasRef} />;
};

export default HeartsEffect;
